import Utils from '../Utils.js';
import Sugerencia from './Sugerencia.js';
import moment from 'moment';
import DocumentoOnline from './Documento.js';
import DocumentoOffline from './DocumentoOffline.js';
import FileUtils from '../FileUtils.js';
import SincronizacionEvents from './SincronizacionEvents.js';
import EstadoRed from './EstadoRed.js';

// Vigilancia de la red durante una sincronización de descarga, compartida por el streaming
// (datos por SSE) y el paquete (datos por GET). El servidor no avisa de que la conexión se ha
// perdido ni de que va lenta: se deduce de cuándo llegó el último evento o trozo de descarga,
// a qué ritmo llegan los bytes y de lo que diga el dispositivo de su propia red.
// Los umbrales distinguen la fase: mientras llegan datos el silencio es sospechoso enseguida;
// mientras el servidor busca (buscando/cola) es lo normal; guardando en local no se vigila.
const RED_LENTA_KBS = 30;             // por debajo de esto, "va lenta"
const RED_LENTA_MS = 8000;            // sin datos durante tanto, "va lenta"
const RED_SIN_RESPUESTA_MS = 30000;   // sin datos durante tanto, "puede haberse perdido"
const SERVIDOR_CALLADO_MS = 20000;    // buscando sin noticias: se dice que es el servidor
const SERVIDOR_MUDO_MS = 90000;       // buscando sin noticias tanto tiempo: ya es sospechoso
const VENTANA_MS = 5000;              // ventana de cálculo de la velocidad
const AVISO_TRANSITORIO_MS = 4000;    // cuánto se enseña "recuperada" / "has pasado a…"

// Colecciones que baja una sincronización, en el orden en que el servidor las prepara, con el
// nombre que ve el inspector. Es lo que permite pintar la lista entera desde el principio y
// que se vea qué está hecho, qué está en marcha y qué falta.
const COLECCIONES_SYNC = [
    ['properties', 'Configuración'],
    ['componentesProteccion', 'Componentes de protección'],
    ['equipos', 'Equipos'],
    ['formatos', 'Formatos'],
    ['inspectores', 'Inspectores'],
    ['provincias', 'Provincias'],
    ['paises', 'Países'],
    ['comunidades', 'Comunidades'],
    ['evaluadoresjs', 'Evaluadores'],
    ['municipios', 'Municipios'],
    ['clientes', 'Clientes'],
    ['expedientes', 'Expedientes']
];

// Guardado de UN documento en la base local. El documento viaja al plugin como cadena JSON
// (plugin >= 0.0.5): Java lo parsea una sola vez y no lo devuelve. Pasándolo como objeto, cada
// guardado de un expediente de 12 MB dejaba ~280 MB vivos en el heap de Java (medido con el
// 28/PCI/06/20008/01/26) y el OutOfMemoryError cerraba la app sin aviso.
// Mongo no admite documentos de más de 16 MB; el BSON ocupa ~5,5 % más que el JSON, así que el
// tope llega hacia los 15,9 millones de caracteres. Se avisa antes, para verlo venir.
const AVISO_TAMANO_DOC = 12.5 * 1024 * 1024;   // caracteres del JSON (~13 MB en BSON)

function megas(caracteres) {
    return (caracteres / 1048576).toFixed(1).replace('.', ',');
}

function mensajeErrorGuardado(error, caracteres) {
    const texto = typeof error === 'string' ? error
        : (error && (error.message || error.error)) || JSON.stringify(error);
    const tam = caracteres ? ' (' + megas(caracteres) + ' MB)' : '';
    if (/OutOfMemoryError/.test(texto)) {
        return 'La tablet se ha quedado sin memoria al guardar' + tam + '. Los últimos cambios NO se han guardado en la tablet: no cierre la inspección, vuelva a guardar y, si se repite, avise a soporte.';
    }
    if (/larger than maximum|BsonMaximumSize|too large/i.test(texto)) {
        return 'El registro es demasiado grande para guardarse en la tablet' + tam + ', el máximo son 16 MB. Los últimos cambios NO se han guardado: avise a soporte antes de seguir.';
    }
    return 'No se ha podido guardar en la tablet: ' + texto;
}

/**
 * Reemplaza (o inserta) el documento que cumple `filtro` y resuelve con `data`, que es lo que
 * antes devolvía el plugin. Si falla, rechaza con `{ message, error, tamano }`: `message` es lo
 * que pinta rForm.
 */
function guardarDocumento(database, collection, filtro, data) {
    let json;
    try {
        json = JSON.stringify(data);
    } catch (e) {
        return Promise.reject({ message: 'No se ha podido preparar el registro para guardarlo: ' + e.message, error: e });
    }
    const tamano = json.length;
    if (tamano > AVISO_TAMANO_DOC) {
        Utils.log('[guardarDocumento] ' + collection + ' ' + JSON.stringify(filtro) + ' ocupa ' + megas(tamano) + ' MB');
        try {
            window.dispatchEvent(new CustomEvent('simecal:guardado-local', {
                detail: { collection: collection, filtro: filtro, tamano: tamano, megas: megas(tamano) }
            }));
        } catch (e) { /* sin CustomEvent no hay aviso, el guardado sigue */ }
    }
    return window.db.replaceOne(database, collection, filtro, json)
        .catch(function(error) {
            // Plugin < 0.0.5 (APK compilada con el plugin viejo): no acepta la cadena. Se guarda
            // como antes, con el objeto, para no dejar la app sin guardar.
            const texto = typeof error === 'string' ? error : (error && error.message) || '';
            if (/JSONException|not a JSONObject|cannot be converted to JSONObject/i.test(texto)) {
                Utils.log('[guardarDocumento] plugin sin cadena JSON (< 0.0.5), se guarda con el objeto');
                return window.db.replaceOne(database, collection, filtro, data);
            }
            return Promise.reject(error);
        })
        .then(function(guardado) {
            if (!guardado) {
                return Promise.reject({ message: 'No se ha actualizado el registro en la tablet.', tamano: tamano });
            }
            return data;
        }, function(error) {
            const message = mensajeErrorGuardado(error, tamano);
            Utils.log('[guardarDocumento] ' + collection + ' ' + JSON.stringify(filtro) + ': ' + message);
            return Promise.reject({ message: message, error: error, tamano: tamano });
        });
}

// Resumen por colección para el diálogo: cada fila tiene estado
// (pendiente | preparando | lista | descargando | enCola | guardando | hecha | error) y un detalle.
// Las tres etapas se ven por separado porque son tres cosas distintas que pueden tardar: el
// servidor consultando Mongo, la red bajando los datos y la tablet guardándolos en local.
function crearResumenSync() {
    let filas = COLECCIONES_SYNC.map(function(c) { return { clave: c[0], nombre: c[1], estado: 'pendiente', detalle: '' }; });
    let porClave = {};
    filas.forEach(function(f) { porClave[f.clave] = f; });
    return {
        filas: filas,
        poner: function(clave, estado, detalle) {
            let f = porClave[clave];
            if (!f) {
                f = { clave: clave, nombre: clave, estado: 'pendiente', detalle: '' };
                filas.push(f);
                porClave[clave] = f;
            }
            f.estado = estado;
            f.detalle = detalle || '';
        },
        hechas: function() { return filas.filter(function(f) { return f.estado === 'hecha'; }).length; },
        /** Copia plana para Vue: el objeto se muta y el componente no vería los cambios. */
        foto: function() {
            return { filas: filas.map(function(f) { return { clave: f.clave, nombre: f.nombre, estado: f.estado, detalle: f.detalle }; }), hechas: this.hechas(), total: filas.length };
        }
    };
}

// "3,2 s" / "1 min 12 s": para que el diálogo diga cuánto ha costado cada guardado.
function duracionTexto(ms) {
    if (ms < 1000) return ms + ' ms';
    let seg = Math.round(ms / 100) / 10;
    if (seg < 60) return String(seg).replace('.', ',') + ' s';
    let min = Math.floor(seg / 60);
    return min + ' min ' + Math.round(seg - min * 60) + ' s';
}

// onProgreso(texto, red, resumen): red = { red, aviso, tipoRed, tipoRedTexto, velocidad },
// red ∈ ok | info | lenta | sin-respuesta | perdida | recuperada; resumen = foto de crearResumenSync.
function crearVigilanteRed(onProgreso) {
    let resumen = crearResumenSync();
    let fase = 'inicio';                // inicio | cola | buscando | datos | guardando
    let faseColeccion = null;
    let ultimoEvento = Date.now();
    let ventana = [];                   // {at, bytes} de lo último recibido
    let sseCaida = false;               // SincronizacionEvents ha avisado de un corte
    let recuperadaEn = 0;
    let cambioRedEn = 0;
    let cambioRedTexto = null;
    let ultimoTexto = '';
    let ticker = null;
    let pararVigilancia = null;
    let parado = false;

    function velocidadKBs() {
        let ahora = Date.now();
        while (ventana.length && ahora - ventana[0].at > VENTANA_MS) ventana.shift();
        if (ventana.length < 2) return null;
        let bytes = 0;
        ventana.forEach(function(v) { bytes += v.bytes; });
        let seg = (ahora - ventana[0].at) / 1000;
        return seg > 0 ? Math.round(bytes / 1024 / seg) : null;
    }

    function estadoRed() {
        let ahora = Date.now();
        let tipoRed = EstadoRed.tipo();
        let r = { red: 'ok', aviso: null, tipoRed: tipoRed, tipoRedTexto: EstadoRed.etiqueta(tipoRed), velocidad: null };
        let silencio = ahora - ultimoEvento;
        let seg = Math.round(silencio / 1000);
        if (sseCaida || tipoRed === 'ninguna') {
            r.red = 'perdida';
            r.aviso = (tipoRed === 'ninguna')
                ? 'Sin cobertura. Esperando a que vuelva la red…'
                : 'Se ha perdido la conexión con el servidor. Reintentando…';
            return r;
        }
        if (recuperadaEn && ahora - recuperadaEn < AVISO_TRANSITORIO_MS) {
            r.red = 'recuperada';
            r.aviso = 'Conexión recuperada.';
            return r;
        }
        if (cambioRedEn && ahora - cambioRedEn < AVISO_TRANSITORIO_MS) {
            r.red = 'info';
            r.aviso = cambioRedTexto;
            return r;
        }
        if (fase === 'datos') {
            r.velocidad = velocidadKBs();
            if (silencio >= RED_SIN_RESPUESTA_MS) {
                r.red = 'sin-respuesta';
                r.aviso = 'Sin respuesta desde hace ' + seg + ' s. La conexión puede haberse perdido.';
            } else if (silencio >= RED_LENTA_MS || (r.velocidad !== null && r.velocidad < RED_LENTA_KBS)) {
                r.red = 'lenta';
                r.aviso = 'La conexión va lenta' + (r.velocidad !== null ? ' (' + r.velocidad + ' KB/s)' : '') + '…';
            }
        } else if (fase === 'buscando' || fase === 'cola') {
            if (silencio >= SERVIDOR_MUDO_MS) {
                r.red = 'lenta';
                r.aviso = 'Sin noticias del servidor desde hace ' + seg + ' s. Si no avanza, comprueba la conexión.';
            } else if (silencio >= SERVIDOR_CALLADO_MS) {
                r.red = 'info';
                r.aviso = (fase === 'cola' ? 'Esperando turno en el servidor' : 'El servidor sigue preparando ' + (faseColeccion || 'los datos')) + ' (' + seg + ' s)…';
            }
        }
        return r;
    }

    function emitir() {
        if (parado || typeof onProgreso !== 'function') return;
        try { onProgreso(ultimoTexto, estadoRed(), resumen.foto()); } catch (e) {}
    }

    return {
        narra: function(txt) { ultimoTexto = txt; emitir(); },
        /** Cambia el estado de una colección en el resumen y repinta. */
        marcar: function(clave, estado, detalle) { resumen.poner(clave, estado, detalle); emitir(); },
        emitir: emitir,
        velocidad: velocidadKBs,
        /** Ha llegado algo (evento SSE o trozo de descarga); bytes cuenta para la velocidad en fase de datos. */
        actividad: function(meta) {
            ultimoEvento = (meta && meta.at) || Date.now();
            if (fase === 'datos' && meta && meta.bytes) ventana.push({ at: ultimoEvento, bytes: meta.bytes });
        },
        fase: function(f, col) {
            if (f !== fase || (col || null) !== faseColeccion) ventana = [];
            fase = f;
            faseColeccion = col || null;
        },
        arrancar: function() {
            SincronizacionEvents.onConexion(function(info) {
                if (info.estado === 'perdida') {
                    sseCaida = true;
                } else if (info.estado === 'recuperada') {
                    sseCaida = false;
                    recuperadaEn = Date.now();
                    ventana = [];
                }
                emitir();
            });
            pararVigilancia = EstadoRed.vigilar(function(cambio) {
                // Pasar de wifi a datos (o al revés) con la descarga en marcha es lo que suele
                // cortar el stream: se dice, para que el corte que venga tenga explicación.
                if (cambio.online && (cambio.tipo === 'wifi' || cambio.tipo === 'datos')
                        && (cambio.anterior === 'wifi' || cambio.anterior === 'datos')) {
                    cambioRedTexto = (cambio.tipo === 'wifi') ? 'Has pasado a Wi-Fi.' : 'Has pasado a datos móviles.';
                    cambioRedEn = Date.now();
                }
                emitir();
            });
            // Reloj: "va lenta" y "sin respuesta" dependen del tiempo que pasa SIN datos, así
            // que no basta con repintar cuando llega algo.
            ticker = setInterval(emitir, 1000);
        },
        /** El SSE se ha cerrado a propósito: su estado deja de contar. */
        olvidarSse: function() { sseCaida = false; },
        parar: function() {
            parado = true;
            if (ticker) { clearInterval(ticker); ticker = null; }
            if (pararVigilancia) { try { pararVigilancia(); } catch (e) {} pararVigilancia = null; }
        }
    };
}

const DatabaseModel = {
    database: "simecaldb",
    getList: function(params) {
        var self = this;
        params = params || {};
        return new Promise(function(resolve, reject) {
            const collection = self.model;
            var order = {};
            if (params.order && params.sortInDatabase) {
                order[params.order] = params.orderDir == 'ASC' ? 1 : -1;
            }
            var projections = "";
            if (params.projection) {
                if (Array.isArray(params.projection) && params.projection.length) {
                    projections = params.projection.join(',');
                } else if (typeof params.projection == 'string') {
                    projections = params.projection;
                } 
            }
            window.db.find(self.database, collection, params.filter || {}, order, params.offset, params.limit, projections)
            .then((result) => {
                window.db.count(self.database, collection, params.filter || {})
                    .then(count => {
                        // si hay params.order, ordenar el resultado en la direccion de params.orderDir
                        if (!params.sortInDatabase && params.order) {
                            result.sort((a, b) => {
                                if (a[params.order] < b[params.order]) {
                                    return params.orderDir == 'ASC' ? -1 : 1;
                                }
                                if (a[params.order] > b[params.order]) {
                                    return params.orderDir == 'ASC' ? 1 : -1;
                                }
                                return 0;
                            });
                        }
                        resolve({ list: result, count });
                }).catch((error) => {
                    console.error(error);
                    reject({error: error});
                });
            })
            .catch((error) => {
                console.error(error);
                reject({error: error});
            });
        });
    },
    get: function(params) {
        var self = this;
        params = params || {};
        return new Promise(function(resolve, reject) {
            const collection = self.model;
            if (!params.itemId) reject({ error: 'itemId not defined' });
            window.db.findOne(self.database, collection, { id: params.itemId })
            .then((result) => {
                resolve( result );
            })
            .catch((error) => {
                console.error(error);
                reject({ error: 'No se encuentra el registro' });
            });
        });
    },
    put: function(params) {
        var self = this;
        params = params || {};
        return new Promise(function(resolve, reject) {
            const collection = self.model;
            if (!params.itemId) reject({ error: 'itemId not defined' });
            delete params.data._id;
            guardarDocumento(self.database, collection, { id: params.itemId }, params.data)
            .then((data) => resolve(data))
            .catch((error) => {
                console.error(error);
                reject(error);
            });
        });
    },
    putAll: function(items) {
        var self = this;
        items = items || [];
        return new Promise(function(resolve, reject) {
            var results = [];
            var p = Promise.resolve();
            const collection = self.model;
            items.forEach(item => {
                p = p.then(_ => new Promise(function(resolve, reject) {
                    delete item._id;
                    guardarDocumento(self.database, collection, {id: item.id}, item)
                    .then((result) => {
                        results.push(result);
                        resolve();
                    }).catch((error) => reject(error));
                }));
            });
            p.then((_) => resolve(results)).catch(e => reject(e));
        });
    },
    post: function(params) {
        var self = this;
        params = params || {};
        return new Promise(function(resolve, reject) {
            const collection = self.model;
            guardarDocumento(self.database, collection, {id: params.data.id}, params.data)
                .then((result) => {
                    resolve(result);
                }).catch((error) => reject(error));
        });
    },
    delete: function(params) {
        var self = this;
        params = params || {};
        return new Promise(function(resolve, reject) {
            const collection = self.model;
            if (!params.itemId) reject({ error: 'itemId not defined' });
            window.db.deleteOne(self.database, collection, {id: params.itemId})
            .then((result) => {
                if (result) {
                    resolve(result);
                } else {
                    reject({ error: "No se ha podido realizar el borrado" });
                }
            })
            .catch((error) => {
                console.error(error);
                reject({error: error});
            });
        });
    },
    syncUp: function(params) {
        var self = this;
        params = params || {};
        return new Promise(function(resolve, reject) {
            if (!params.data) {
                reject({ error: 'Datos no definidos' });
            }
            // var albaran = JSON.parse(JSON.stringify(params.data));
            // albaran.albaranFirmado = params.data.albaranFirmado;

            // if (!params.sugerencia) {
            //     if (albaran.id.startsWith('local')) {
            //         delete albaran.id;
            //     } 
            //     delete albaran._id;
            //     delete albaran.$loki;
            //     delete albaran.meta;
            // }

            self.model = "inspecciones";
            let inspeccionesIds = params.data.map(i => i.id || i._id);
            return self.getList({filter: {id: {"$in": inspeccionesIds}}}).then(function(res) {
                if (res.error) {
                    reject(data);
                    return;
                }
                let inspecciones = res.list;
                var error = self.comprobarExpedientes(inspecciones);
                if (error && error.idExp) {
                    reject(error);
                    return;
                }


                // if (params.sugerencia) {
                //     albaran.expedientes = inspecciones;
                //     return Sugerencia.post({
                //         data: { sugerencia: JSON.stringify(albaran), albaranMalo: true}
                //       }).then(data => resolve(data)).catch( e => reject(e));

                // }
                for (let i = 0; i < inspecciones.length; i++) {
                    var inspeccion = inspecciones[i];
                    if (inspeccion.id.startsWith('local')) {
                        delete inspecciones[i].id;
                    }
                    delete inspecciones[i].$loki;
                    delete inspecciones[i]._id;
                    delete inspecciones[i].meta;
                    delete inspecciones[i].idAlbaran;
                    delete inspecciones[i].codigoAlbaran;
                    Utils.limpiarInstruccionesComprobaciones(inspecciones[i].comprobaciones);
                    // Aligeramos el formato embebido antes de subirlo: quita listadoCondicionesAuditoria
                    // (reglas), condicionesAuditoria y demás datos pesados. El servidor no los necesita
                    // (busca el formato por código en BD), y así no viajan al servidor.
                    Utils.limpiarFormatoExpediente(inspecciones[i].formato);
                    // Y por si el formato completo se ha recolado en otros sitios (formatoOrigen,
                    // historicoOffline, etc. al evaluar reglas), quitamos las condiciones de auditoría
                    // en CUALQUIER nivel del expediente antes de subirlo.
                    Utils.eliminarClavesProfundo(inspecciones[i], ['listadoCondicionesAuditoria', 'condicionesAuditoria']);
                    // Versión de la app con la que se sincroniza/realiza la inspección.
                    inspecciones[i].versionApp = process.env.BUILDDATE;
                }
                var inspeccionesString = JSON.stringify(inspecciones);
                while (inspeccionesString.match(/\{\"\$numberLong\":\"(.*)\"\}/)) {
                    inspeccionesString = inspeccionesString.replace(/\{\"\$numberLong\":\"([0-9]+)\"\}/,'$1');
                }
                inspecciones = JSON.parse(inspeccionesString);
                // albaran.expedientes = inspecciones;
                return self.uploadDocuments(inspecciones).then(function(data) {
                    return self.request.promise({
                        url: self.server_movilidad + self.prefix + 'expediente/sync/',
                        method: 'POST',
                        contentType: 'application/json',
                        dataType: 'json',
                        crossDomain: true,
                        processData: false,
                        data: JSON.stringify(data)
                    }).then(function({ data, xhr, status }) {
                        if (data.error) {
                            reject(data);
                        } else {
                            var p = Promise.resolve();
                            data.list.forEach(function(item, idx) {
                                item.sincronizado = true;
                                delete item._id;
                                Utils.moveInspeccionToBorrar(item, inspeccionesIds[idx]);
                                var today = moment().format("YYYYMMDD"),
                                    provincia = item.direccionInspeccion && item.direccionInspeccion.provincia ? item.direccionInspeccion.provincia : "00",
                                    formato = item.formato.codigo.replaceAll(/\s/g, ""),
                                    newName = item.codigo ? item.codigo.replaceAll("/", "_") : '',
                                    folder = `Documents/SIMECAL/${formato}/${provincia}/${inspeccionesIds[idx]}`,
                                    newFolder = `Documents/SIMECAL/Borrar/${today}/${newName}`;
                                self._getAllDocuments(item).forEach(doc => {
                                    doc.offlinePath = doc.offlinePath ? doc.offlinePath.replace(folder, newFolder) : '';
                                })
                                p = p.then(_ => new Promise(function(resolve, reject) {
                                    guardarDocumento(self.database, 'inspecciones', {id: inspeccionesIds[idx]}, item)
                                    .then((_) => {
                                        resolve();
                                    }).catch((error) => reject(error));
                                }));
                            });
                            p.then((_) => {
                                // data.list = data.dto.expedientes.map(e => e.id);
                                self.getList({filter: {id: {"$in": inspeccionesIds}}}).then(function(result) {
                                    resolve(result.list);
                                }).catch((error) => reject(error));
                            }).catch(e => reject(e));
                        }
                    }).catch( function(e) {
                        self._catchError(e, reject);
                    });
                }).catch(function(e) {
                    self._catchError(e, reject);
                });
            }).catch( function(e) {
                self._catchError(e, reject);
            });
        });
    },
    syncUpAlbaran: function(params) {
        var self = this;
        params = params || {};
        return new Promise(function(resolve, reject) {
            if (!params.data) {
                reject({ error: 'Datos no definidos' });
            }
            var albaran = JSON.parse(JSON.stringify(params.data));
            albaran.albaranFirmado = params.data.albaranFirmado;

            if (!params.sugerencia) {
                if (albaran.id.startsWith('local')) {
                    delete albaran.id;
                } 
                delete albaran._id;
                delete albaran.$loki;
                delete albaran.meta;
            }

            self.model = "inspecciones";
            return self.getList({filter: {id: {"$in": albaran.expedientes}}}).then(function(res) {
                if (res.error) {
                    reject(data);
                    return;
                }
                var inspecciones = res.list;
                var error = self.comprobarExpedientes(inspecciones);
                if (error && error.idExp) {
                    reject(error);
                    return;
                }


                if (params.sugerencia) {
                    albaran.expedientes = inspecciones;
                    return Sugerencia.post({
                        data: { sugerencia: JSON.stringify(albaran), albaranMalo: true}
                      }).then(data => resolve(data)).catch( e => reject(e));

                }
                for (let i = 0; i < inspecciones.length; i++) {
                    var inspeccion = inspecciones[i];
                    if (inspeccion.id.startsWith('local')) {
                        delete inspecciones[i].id;
                    } 
                    delete inspecciones[i].$loki;
                    delete inspecciones[i]._id;
                    delete inspecciones[i].meta;
                    delete inspecciones[i].idAlbaran;
                    delete inspecciones[i].codigoAlbaran;
                }
                var inspeccionesString = JSON.stringify(inspecciones);
                while (inspeccionesString.match(/\{\"\$numberLong\":\"(.*)\"\}/)) {
                    inspeccionesString = inspeccionesString.replace(/\{\"\$numberLong\":\"([0-9]+)\"\}/,'$1');
                    
                }
                inspecciones = JSON.parse(inspeccionesString);
                albaran.expedientes = inspecciones;
                return self.uploadDocuments(albaran).then(function(data) {
                    return self.request.promise({
                        url: self.server_movilidad + self.prefix + 'albaran/sync/',
                        method: 'POST',
                        contentType: 'application/json',
                        dataType: 'json',
                        crossDomain: true,
                        processData: false,
                        data: JSON.stringify(data)
                    }).then(function({ data, xhr, status }) {
                        if (data.error) {
                            reject(data);
                        } else {
                            var p = Promise.resolve();
                            data.dto.expedientes.forEach(function(item, idx) {
                                item.sincronizado = true;
                                delete item._id;
                                p = p.then(_ => new Promise(function(resolve, reject) {
                                    guardarDocumento(self.database, 'inspecciones', {id: params.data.expedientes[idx]}, item)
                                    .then((_) => {
                                        resolve();
                                    }).catch((error) => reject(error));
                                }));
                            });
                            p.then((_) => {
                                data.dto.expedientes = data.dto.expedientes.map(e => e.id);
                                guardarDocumento(self.database, 'albaranes', {id: params.data.id}, data.dto)
                                .then((result) => {
                                    resolve(result);
                                }).catch((error) => reject(error));
                            }).catch(e => reject(e));
                        }
                    }).catch( function(e) {
                        self._catchError(e, reject);
                    });
                }).catch(function(e) {
                    self._catchError(e, reject);
                });
            }).catch( function(e) {
                self._catchError(e, reject);
            });
        });
    },
    expMalo: function (expediente) {
        return Sugerencia.post({
            data: { sugerencia: JSON.stringify(expediente), expedienteMalo: true}
          });
        
    },
    cleanData: function() {
        const self = this;
        var p = Promise.resolve();
        ['configuraciones', 'componentesProteccion', 'equipos', 'clientes', 'formatos', 'inspectores', 'municipios', 'provincias', 'paises', 'comunidades', 'evaluadoresjs'].forEach(function(el) {
            p = p.then(_ => new Promise(function(resolve, reject) {
            window.db.deleteAll(self.database, el)
                .then(_ => resolve())
                .catch((error) => reject(error));
            }));
        });
        p = p.then(_ => new Promise(function(resolve, reject) {
            window.db.deleteMany(self.database, 'inspecciones', { codigo: { $exists: false }, guardadoLocal: { $exists: false } })
            .then(_ => resolve())
            .catch(_ => resolve());
        }));
        return p;
    },
    sync: function(params) {
        const self = this;
        params = params || {};
        params.data = params.data || {};
        var p = Promise.resolve();
        p = p.then(_ => new Promise(function(resolve, _) {
            window.db.findOne(self.database, 'configuraciones', {id: 'CONFIG'})
            .then(result => resolve(result))
            .catch(_ => resolve(null));
        }));
        p = p.then(configuracion => new Promise(function(resolve, _) {
            if (configuracion) {
                params.data.syncDate = configuracion.syncDate;
                params.data.dispositivo = configuracion.dispositivo;
            }
            if (!params.data.dispositivo) {
                if (typeof device != "undefined") {
                    params.data.dispositivo = device;
                } else {
                    params.data.dispositivo = {};
                    params.data.dispositivo.uuid = "WEB_" + window.app.data.user.username;
                }
            }
            resolve(configuracion);
        }));

        p = p.then(configuracion => new Promise(function(resolve, reject) {
            self.request.promise({
                url: self.server_movilidad + self.prefix + 'sincronizacion/',
                method: 'POST',
                contentType: 'application/json',
                dataType: 'json',
                crossDomain: true,
                processData: false,
                data: JSON.stringify(params.data)
            }).then(function({ data, xhr, status }) {
                if (data.error) {
                    reject(data);
                } else {

                    if (data.dto.properties) {
                        localStorage.setItem('properties', JSON.stringify(data.dto.properties));
                    } else {
                        localStorage.setItem('properties', JSON.stringify([]));
                    }

                    var p = Promise.resolve();
                    ['componentesProteccion', 'equipos', 'formatos', 'inspectores', 'municipios', 'provincias', 'paises', 'comunidades', 'evaluadoresjs', 'clientes'].forEach(function(el) {
                        p = p.then(_ => new Promise(function(resolve, reject) {
                            // Se comenta ya que ahora los municipios no tiene gfecha modificacion y siempre devuelven todos, 
                            // Cuando esto se arregle, debería descomentarse esta linea
                            if (el == 'clientes' || el == 'municipios') {
                            //if (el == 'clientes') {
                                self._replaceInCollection(el, data.dto[el], resolve, reject, params.data.syncDate);
                            } else {
                                self._reloadCollection(el, data.dto[el], resolve, reject);
                            }
                        }));
                    });
                    
                    p = p.then(_ => new Promise(function(resolve, reject) {
                        window.db.deleteMany(self.database, 'inspecciones', { codigo: { $exists: false }, guardadoLocal: { $exists: false } })
                        .then(_ => resolve())
                        .catch(_ => resolve());
                    }));

                    // p = p.then(_ => new Promise(function(resolve, reject) {
                    //     window.db.deleteMany(self.database, 'inspecciones', { 'cliente.razonSocial': { $exists: false } })
                    //     .then(_ => resolve())
                    //     .catch(_ => resolve());
                    // }));

                    p = p.then(_ => new Promise(function(resolve, reject) {
                        window.db.deleteMany(self.database, 'inspecciones', { $and: [{ codigo: {$exists: true}, fechaInsp: {$lte: moment(new Date()).add(-7,"day").format()} }] })
                        .then(_ => resolve())
                        .catch(_ => resolve());
                    }));

                    p = p.then(_ => new Promise(function(resolve, reject) {
                        self._insertNotExistsInCollection('inspecciones', data.dto['expedientes'], resolve, reject);
                    }));
                    

                    p = p.then(_ => new Promise(function(resolve, reject) {
                        var dispositivo = data.dto['dispositivo'];
                        var syncDate = new Date();

                        if (!configuracion) {
                            configuracion = {id: "CONFIG"};
                        }
                        configuracion.syncDate = syncDate;
                        var dispositivoActual = configuracion.dispositivo;
                        if (dispositivoActual && dispositivo.anyo == dispositivoActual.anyo && dispositivo.secAlbaran > dispositivoActual.secAlbaran) {
                            dispositivoActual.secAlbaran = dispositivo.secAlbaran;
                        } else if (!dispositivoActual) {
                            configuracion.dispositivo = dispositivo;
                        } else {
                            configuracion.dispositivo = dispositivoActual;
                        }
                        guardarDocumento(self.database, "configuraciones", {id: 'CONFIG'}, configuracion)
                        .then((result) => {
                            resolve();
                        }).catch(e => self._catchError(e, reject));
                    }));
                    
                    p.then(_ => resolve());
                    p.catch(e => self._catchError(e, reject));
                    return p;
                }
            }).catch(function(e) {
                self._catchError(e, reject);
            });
        }));
        p = p.then(_ => new Promise(function(resolve, _) {
            Utils.removeBackups();
            resolve();
        }));
        return p;
    },
    // Sincronización de descarga en STREAMING: dispara el trabajo en el back (que va emitiendo
    // cada colección por SSE) y guarda cada colección según llega, reutilizando los mismos
    // helpers de guardado que sync(). Al final verifica los totales por colección; si falta algo
    // (p.ej. por un corte del stream) NO marca la sincronización como completada y rechaza.
    // onProgreso(texto, red) es un callback opcional para narrar el avance en la UI; `red` es el
    // estado de la conexión que se deduce aquí (ver estadoRed).
    syncStreaming: function(params, onProgreso) {
        const self = this;
        params = params || {};
        params.data = params.data || {};

        let buffers = {};        // acumulador de items por colección mientras llegan sus lotes
        let expected = {};       // totales anunciados por el back (coleccion-fin / fin.counts)
        let saved = {};          // items realmente guardados por colección
        let configuracion = null;
        let dispositivo = null;
        let terminado = false;
        let saveChain = Promise.resolve();

        let vig = crearVigilanteRed(onProgreso);
        function narra(txt) { vig.narra(txt); }

        // Promesa diferida en vez de envolver todo el cuerpo en el ejecutor de `new Promise`: la
        // envoltura sumaba un nivel de anidamiento a todas las funciones de dentro.
        let resolve = null;
        let reject = null;
        const promesa = new Promise(function(res, rej) { resolve = res; reject = rej; });
        {

            function terminar(err) {
                if (terminado) return;
                terminado = true;
                vig.parar();
                SincronizacionEvents.unsubscribe(handler);
                SincronizacionEvents.close();
                if (err) {
                    self._catchError(err, reject);
                } else {
                    Utils.removeBackups();
                    resolve();
                }
            }

            // Guarda una colección completa reutilizando la misma semántica que sync().
            function guardarColeccion(coleccion, list) {
                return self._guardarColeccionSync(coleccion, list, params.data.syncDate);
            }

            function guardarConfig() {
                return self._guardarConfigSync(configuracion, dispositivo);
            }

            function handler(payload, meta) {
                if (!payload || terminado) return;
                let estado = payload.estado;
                let col = payload.coleccion;
                if (estado === 'buscando') {
                    // El servidor avisa de "cola" con este mismo estado antes de empezar.
                    vig.fase((col === 'cola') ? 'cola' : 'buscando', col);
                    vig.actividad(meta);
                    if (col && col !== 'cola' && col !== 'dispositivo') vig.marcar(col, 'preparando', 'preparando en el servidor…');
                    narra(payload.texto || ('Buscando ' + col + '…'));
                } else if (estado === 'datos') {
                    vig.fase('datos', col);
                    vig.actividad(meta);
                    buffers[col] = (buffers[col] || []).concat(payload.items || []);
                    let recibidos = (payload.recibidos != null) ? payload.recibidos : buffers[col].length;
                    let total = (payload.total != null) ? payload.total : recibidos;
                    let v = vig.velocidad();
                    vig.marcar(col, 'descargando', recibidos + '/' + total + (payload.totalLotes ? ' · lote ' + payload.lote + '/' + payload.totalLotes : '') + (v !== null ? ' · ' + v + ' KB/s' : ''));
                    if (col !== 'properties' && col !== 'dispositivo') {
                        narra('Recibiendo ' + col + '… ' + recibidos + '/' + total + (v !== null ? ' · ' + v + ' KB/s' : ''));
                    }
                } else if (estado === 'coleccion-fin') {
                    // El servidor anuncia la siguiente colección acto seguido.
                    vig.fase('buscando', null);
                    vig.actividad(meta);
                    expected[col] = payload.total || 0;
                    vig.marcar(col, 'enCola', (payload.total || 0) + ' · recibida, esperando para guardar');
                    let list = buffers[col] || [];
                    buffers[col] = null;
                    // Capturamos la longitud AHORA: los helpers de guardado (_insertMany /
                    // _replaceInCollection) usan list.splice(...) y dejan el array vacío.
                    let n = list.length;
                    saveChain = saveChain.then(function() {
                        // Se narra cuando empieza de verdad: la cola de guardado va por detrás del stream.
                        vig.marcar(col, 'guardando', 'guardando ' + n + ' en la tablet…');
                        if (col !== 'properties') { narra('Guardando ' + col + ' en el dispositivo… (' + n + ')'); }
                        let t0 = Date.now();
                        return guardarColeccion(col, list).then(function() {
                            saved[col] = n;
                            vig.marcar(col, 'hecha', n + ' guardados en ' + duracionTexto(Date.now() - t0));
                        });
                    });
                } else if (estado === 'fin') {
                    dispositivo = payload.dispositivo;
                    let counts = payload.counts || expected;
                    saveChain = saveChain.then(function() {
                        let faltan = [];
                        Object.keys(counts).forEach(function(k) {
                            let exp = counts[k] || 0;
                            let got = (saved[k] != null) ? saved[k] : 0;
                            if (got !== exp) { faltan.push(k + ' (' + got + '/' + exp + ')'); }
                        });
                        if (faltan.length) {
                            throw { error: 'Sincronización incompleta, faltan datos: ' + faltan.join(', ') + '. Vuelve a sincronizar.' };
                        }
                        return guardarConfig();
                    }).then(function() {
                        terminar(null);
                    }).catch(function(e) {
                        terminar(e);
                    });
                } else if (estado === 'error') {
                    saveChain.then(function() {
                        terminar((payload && payload.texto) ? { error: payload.texto } : { error: 'Error en la sincronización' });
                    });
                }
            }

            // 1) Cargamos la config actual (syncDate + dispositivo) igual que sync().
            window.db.findOne(self.database, 'configuraciones', { id: 'CONFIG' })
                .then(function(cfg) { arrancar(cfg); })
                .catch(function() { arrancar(null); });

            function arrancar(cfg) {
                configuracion = cfg;
                if (cfg) {
                    params.data.syncDate = cfg.syncDate;
                    params.data.dispositivo = cfg.dispositivo;
                }
                if (!params.data.dispositivo) {
                    if (typeof device != 'undefined') {
                        params.data.dispositivo = device;
                    } else {
                        params.data.dispositivo = { uuid: 'WEB_' + window.app.data.user.username };
                    }
                }
                narra('Iniciando sincronización…');
                // 2) Nos suscribimos y, SOLO cuando el SSE está conectado (evento "hello"),
                //    lanzamos el disparador HTTP; así el emisor del usuario ya existe en el back.
                SincronizacionEvents.subscribe(handler);
                vig.arrancar();
                SincronizacionEvents.onReady(function() {
                    self.request.promise({
                        url: self.server_movilidad + self.prefix + 'sincronizacion/stream',
                        method: 'POST',
                        contentType: 'application/json',
                        dataType: 'json',
                        crossDomain: true,
                        processData: false,
                        data: JSON.stringify(params.data)
                    }).then(function({ data }) {
                        if (data && data.error) { terminar(data); return; }
                        if (data && data.dto && data.dto.lanzado === false) {
                            terminar({ error: 'Ya hay una sincronización en curso. Espera a que termine.' });
                        }
                        // Si lanzado === true no hacemos nada: la finalización llega por el evento SSE "fin".
                    }).catch(function(e) {
                        terminar(e);
                    });
                });
            }
        }
        return promesa;
    },
    // Guarda una colección completa con la misma semántica que sync(). La usan el streaming y
    // el paquete: lo que cambia entre los dos es cómo llegan los datos, no cómo se guardan.
    _guardarColeccionSync: function(coleccion, list, syncDate) {
        let self = this;
        return new Promise(function(res, rej) {
            list = list || [];
            if (coleccion === 'properties') {
                try { localStorage.setItem('properties', JSON.stringify(list)); } catch (e) {}
                return res();
            }
            if (coleccion === 'expedientes') {
                // Mismo saneado que sync() antes del upsert de inspecciones.
                window.db.deleteMany(self.database, 'inspecciones', { codigo: { $exists: false }, guardadoLocal: { $exists: false } })
                    .catch(function() {})
                    .then(function() {
                        return window.db.deleteMany(self.database, 'inspecciones', { $and: [{ codigo: { $exists: true }, fechaInsp: { $lte: moment(new Date()).add(-7, 'day').format() } }] }).catch(function() {});
                    })
                    .then(function() {
                        self._insertNotExistsInCollection('inspecciones', list, res, rej);
                    });
                return;
            }
            if (coleccion === 'clientes' || coleccion === 'municipios') {
                // Fusión por modificationDate (no recarga completa): igual que sync().
                self._replaceInCollection(coleccion, list, res, rej, syncDate);
                return;
            }
            // Resto: recarga completa (deleteAll + insert).
            self._reloadCollection(coleccion, list, res, rej);
        });
    },
    // Guarda la configuración al terminar: syncDate de ahora y el dispositivo que dio el servidor.
    _guardarConfigSync: function(configuracion, dispositivo) {
        let self = this;
        return new Promise(function(res, rej) {
            let syncDate = new Date();
            if (!configuracion) { configuracion = { id: 'CONFIG' }; }
            configuracion.syncDate = syncDate;
            let dispositivoActual = configuracion.dispositivo;
            if (dispositivoActual && dispositivo && dispositivo.anyo == dispositivoActual.anyo && dispositivo.secAlbaran > dispositivoActual.secAlbaran) {
                dispositivoActual.secAlbaran = dispositivo.secAlbaran;
                configuracion.dispositivo = dispositivoActual;
            } else if (!dispositivoActual && dispositivo) {
                configuracion.dispositivo = dispositivo;
            }
            guardarDocumento(self.database, 'configuraciones', { id: 'CONFIG' }, configuracion)
                .then(function() { res(); })
                .catch(function(e) { rej(e); });
        });
    },
    // Sincronización de descarga por PAQUETE: el servidor prepara los datos en segundo plano
    // (narrando por SSE qué colección está lista) y la tablet los descarga por GET lote a lote,
    // a su ritmo, con reintento por lote. Un corte ya no obliga a repetir la sync: se repite
    // solo el lote que falló. Si el SSE pierde algún aviso, el manifiesto del paquete (GET)
    // cuenta lo mismo. Si el servidor no conoce el endpoint (API antigua), rechaza con
    // { fallbackStreaming: true } para que el llamante use syncStreaming.
    syncPaquete: function(params, onProgreso) {
        const self = this;
        params = params || {};
        params.data = params.data || {};

        let PARALELO = 2;               // descargas a la vez: dos aprovechan la latencia sin saturar
        let SONDEO_MS = 10000;          // cada cuánto se pide el manifiesto mientras se prepara
        let ESPERA_SIN_RED_MS = 3000;   // sin red: reintento suave hasta que vuelva
        let MAX_INTENTOS = 8;           // con red: reintentos con espera creciente antes de rendirse

        let vig = crearVigilanteRed(onProgreso);
        let colecciones = {};           // nombre -> { total, totalLotes, estado:{lote}, buffers:{lote}, hechos, recibidos, guardando }
        let cola = [];                  // lotes pendientes de descargar [{col, lote}]
        let enVuelo = 0;
        let expected = {};
        let saved = {};
        let finCounts = null;
        let configuracion = null;
        let dispositivo = null;
        let paqueteId = null;
        let preparado = false;
        let sseCerrado = false;
        let terminado = false;
        let verificando = false;
        let saveChain = Promise.resolve();
        let sondeo = null;

        function cabeceras() {
            try {
                let session = JSON.parse(localStorage.getItem('session') || 'null');
                let token = session && (session.token || session.access_token);
                return token ? { Authorization: 'Bearer ' + token } : {};
            } catch (e) { return {}; }
        }
        function urlPaquete(sufijo) {
            return self.server_movilidad + self.prefix + 'sincronizacion/paquete' + (sufijo || '');
        }

        // Promesa diferida en vez de envolver todo el cuerpo en el ejecutor de `new Promise`: la
        // envoltura sumaba un nivel de anidamiento a todas las funciones de dentro.
        let resolve = null;
        let reject = null;
        const promesa = new Promise(function(res, rej) { resolve = res; reject = rej; });
        {

            function cerrarSse() {
                if (sseCerrado) return;
                sseCerrado = true;
                SincronizacionEvents.unsubscribe(handler);
                SincronizacionEvents.close();
                vig.olvidarSse();
            }

            function terminar(err) {
                if (terminado) return;
                terminado = true;
                vig.parar();
                if (sondeo) { clearInterval(sondeo); sondeo = null; }
                cerrarSse();
                if (paqueteId) {
                    // Libera la memoria del servidor sin esperar a que caduque. Si falla, caduca sola.
                    try {
                        self.request.promise({ url: urlPaquete('/' + paqueteId + '/fin'), method: 'POST', dataType: 'json', crossDomain: true, headers: cabeceras() })
                            .catch(function() {});
                    } catch (e) {}
                }
                if (err) {
                    self._catchError(err, reject);
                } else {
                    Utils.removeBackups();
                    resolve();
                }
            }

            function registrarColeccion(m) {
                if (!m || !m.coleccion || colecciones[m.coleccion]) return;
                let lotes = m.lotes || [];
                let c = { total: m.total || 0, totalLotes: m.totalLotes || lotes.length, estado: {}, buffers: {}, hechos: 0, recibidos: 0, guardando: false };
                colecciones[m.coleccion] = c;
                expected[m.coleccion] = c.total;
                vig.marcar(m.coleccion, 'lista', c.total + ' · lista para descargar');
                lotes.forEach(function(l) {
                    c.estado[l.lote] = 'pendiente';
                    cola.push({ col: m.coleccion, lote: l.lote });
                });
                if (!c.totalLotes) { completarColeccion(m.coleccion); }
                bombear();
            }

            function bombear() {
                if (terminado) return;
                // descargar() incrementa enVuelo y shift() vacía la cola: el bucle termina solo.
                while (enVuelo < PARALELO && cola.length) {
                    let t = cola.shift();
                    descargar(t.col, t.lote, t.intento || 0);
                }
            }

            function narrarDescarga(col) {
                let c = colecciones[col];
                let v = vig.velocidad();
                let vel = (v !== null) ? ' · ' + v + ' KB/s' : '';
                vig.marcar(col, 'descargando', c.recibidos + '/' + c.total + ' · lote ' + Math.min(c.hechos + 1, c.totalLotes) + '/' + c.totalLotes + vel);
                vig.narra('Descargando ' + col + '… ' + c.recibidos + '/' + c.total + vel);
            }

            function descargar(col, lote, intento) {
                let c = colecciones[col];
                enVuelo++;
                c.estado[lote] = 'bajando';
                vig.fase('datos', col);
                let cargado = 0;
                self.request.promise({
                    url: urlPaquete('/' + paqueteId + '/' + col + '/' + lote),
                    method: 'GET',
                    dataType: 'json',
                    crossDomain: true,
                    headers: cabeceras(),
                    beforeSend: function(xhr) {
                        // El progreso del XHR es lo que permite ver la velocidad real y distinguir
                        // "va lento" de "se ha parado" dentro de un lote grande (los de formatos pesan MB).
                        try {
                            xhr.onprogress = function(ev) {
                                if (ev.loaded > cargado) {
                                    vig.actividad({ at: Date.now(), bytes: ev.loaded - cargado });
                                    cargado = ev.loaded;
                                    narrarDescarga(col);
                                }
                            };
                        } catch (e) {}
                    }
                }).then(function(res) {
                    let data = res && res.data;
                    if (!Array.isArray(data)) {
                        // El servidor responde con ResponseDto de error (paquete caducado, lote inexistente…).
                        let msg = (data && data.error && (data.error.message || data.error)) || 'Respuesta inesperada del servidor';
                        const fatal = new Error(msg);
                        fatal.fatal = true;
                        throw fatal;
                    }
                    enVuelo--;
                    vig.actividad({ at: Date.now(), bytes: 0 });
                    c.buffers[lote] = data;
                    c.estado[lote] = 'ok';
                    c.hechos++;
                    c.recibidos += data.length;
                    narrarDescarga(col);
                    if (c.hechos >= c.totalLotes) completarColeccion(col);
                    bombear();
                }).catch(function(e) {
                    enVuelo--;
                    if (terminado) return;
                    if (e && e.fatal) { terminar({ error: e.message }); return; }
                    if (e && e.status == 401) { terminar(e); return; }
                    // Sin red no cuenta como intento: se espera a que vuelva y se repite el mismo lote.
                    let sinRed = !EstadoRed.online() || (e && e.status === 0);
                    if (!sinRed && intento + 1 >= MAX_INTENTOS) {
                        terminar({ error: 'No se ha podido descargar ' + col + ' (lote ' + lote + ') tras ' + MAX_INTENTOS + ' intentos. Comprueba la conexión y vuelve a sincronizar.' });
                        return;
                    }
                    let espera = sinRed ? ESPERA_SIN_RED_MS : Math.min(30000, 2000 * Math.pow(2, intento));
                    c.estado[lote] = 'pendiente';
                    vig.marcar(col, 'descargando', 'reintentando el lote ' + lote + '/' + c.totalLotes + '…');
                    vig.narra('Reintentando ' + col + ' (lote ' + lote + ')…');
                    // El reintento vuelve a la cola y lo arranca la bomba cuando haya hueco. Si el
                    // temporizador llamara a descargar() directo, con los dos huecos ya ocupados
                    // saldría una tercera descarga, justo en la conexión que peor está.
                    setTimeout(function() {
                        if (terminado) return;
                        cola.unshift({ col: col, lote: lote, intento: sinRed ? intento : intento + 1 });
                        bombear();
                    }, espera);
                    bombear();
                });
            }

            function completarColeccion(col) {
                let c = colecciones[col];
                if (c.guardando) return;
                c.guardando = true;
                let list = [];
                for (let i = 1; i <= c.totalLotes; i++) {
                    list = list.concat(c.buffers[i] || []);
                    c.buffers[i] = null;
                }
                let n = list.length;
                // El guardado va en cola y puede ir muy por detrás de la descarga (clientes en
                // una primera sync son decenas de miles): se narra cuando EMPIEZA de verdad, no
                // al encolarlo, para que la pantalla no se quede clavada en la última colección.
                vig.marcar(col, 'enCola', n + ' · descargada, esperando para guardar');
                saveChain = saveChain.then(function() {
                    if (terminado) return;
                    vig.marcar(col, 'guardando', 'guardando ' + n + ' en la tablet…');
                    if (col !== 'properties') {
                        vig.narra('Guardando ' + col + ' en el dispositivo… (' + n + ')');
                        // Guardar en local puede tardar (formatos); no es la red, que no se alarme.
                        if (!cola.length && !enVuelo) vig.fase('guardando', col);
                    }
                    let t0 = Date.now();
                    return self._guardarColeccionSync(col, list, params.data.syncDate).then(function() {
                        saved[col] = n;
                        vig.marcar(col, 'hecha', n + ' guardados en ' + duracionTexto(Date.now() - t0));
                    });
                }).catch(function(e) { vig.marcar(col, 'error', 'error al guardar'); terminar(e); });
                comprobarFin();
            }

            function comprobarFin() {
                if (terminado || !preparado || verificando) return;
                let nombres = Object.keys(finCounts || expected);
                for (let i = 0; i < nombres.length; i++) {
                    let c = colecciones[nombres[i]];
                    if (!c || !c.guardando) return; // falta por anunciar o por descargar
                }
                verificando = true;
                saveChain.then(function() {
                    let counts = finCounts || expected;
                    let faltan = [];
                    Object.keys(counts).forEach(function(k) {
                        let exp = counts[k] || 0;
                        let got = (saved[k] != null) ? saved[k] : 0;
                        if (got !== exp) { faltan.push(k + ' (' + got + '/' + exp + ')'); }
                    });
                    if (faltan.length) {
                        throw { error: 'Sincronización incompleta, faltan datos: ' + faltan.join(', ') + '. Vuelve a sincronizar.' };
                    }
                    return self._guardarConfigSync(configuracion, dispositivo);
                }).then(function() {
                    terminar(null);
                }).catch(function(e) {
                    terminar(e);
                });
            }

            function faltanColecciones() {
                return Object.keys(finCounts || {}).some(function(k) { return !colecciones[k]; });
            }

            function pararSondeo() {
                if (sondeo) { clearInterval(sondeo); sondeo = null; }
            }

            function marcarPreparado(counts, disp) {
                if (preparado) return;
                preparado = true;
                finCounts = counts || null;
                if (disp) dispositivo = disp;
                // Ya no hace falta el SSE: lo que queda es descargar, y así un corte del stream
                // no se confunde con un corte de la descarga.
                cerrarSse();
                if (faltanColecciones()) {
                    // Se perdió algún aviso de colección por el SSE: el sondeo del manifiesto
                    // SIGUE hasta que estén todas. Si una petición falla, la siguiente vuelta lo
                    // reintenta; con un único disparo, un fallo pasajero dejaba la sync abierta
                    // sin nadie que fuera a por la colección que faltaba.
                    if (!sondeo) sondeo = setInterval(sondear, SONDEO_MS);
                    sondear();
                } else {
                    pararSondeo();
                    comprobarFin();
                }
            }

            function aplicarManifiesto(m) {
                if (!m || terminado) return;
                if (m.paquete && !paqueteId) paqueteId = m.paquete;
                (m.colecciones || []).forEach(registrarColeccion);
                if (m.estado === 'ERROR') {
                    terminar({ error: m.error || 'Error preparando la sincronización en el servidor.' });
                } else if (m.estado === 'LISTO') {
                    marcarPreparado(m.counts, m.dispositivo);
                    if (!faltanColecciones()) pararSondeo();
                    comprobarFin();
                }
            }

            function sondear() {
                if (terminado || !paqueteId) return;
                self.request.promise({ url: urlPaquete('/' + paqueteId), method: 'GET', dataType: 'json', crossDomain: true, headers: cabeceras() })
                    .then(function(res) {
                        let d = res && res.data;
                        if (d && d.error) { terminar(d); return; }
                        aplicarManifiesto(d && d.dto);
                    })
                    .catch(function() { /* el siguiente sondeo lo reintenta */ });
            }

            function handler(payload, meta) {
                if (!payload || terminado) return;
                let estado = payload.estado;
                let col = payload.coleccion;
                vig.actividad(meta);
                if (payload.paquete && paqueteId && payload.paquete !== paqueteId) return; // de otra sync
                if (payload.paquete && !paqueteId) paqueteId = payload.paquete;
                if (estado === 'buscando') {
                    vig.fase((col === 'cola') ? 'cola' : 'buscando', col);
                    if (col && col !== 'cola' && col !== 'dispositivo') vig.marcar(col, 'preparando', 'preparando en el servidor…');
                    vig.narra(payload.texto || ('Buscando ' + col + '…'));
                } else if (estado === 'coleccion-lista') {
                    registrarColeccion(payload);
                } else if (estado === 'fin') {
                    marcarPreparado(payload.counts, payload.dispositivo);
                } else if (estado === 'error') {
                    terminar({ error: payload.texto || 'Error en la sincronización' });
                }
            }

            // 1) Config actual (syncDate + dispositivo), igual que sync().
            window.db.findOne(self.database, 'configuraciones', { id: 'CONFIG' })
                .then(function(cfg) { arrancar(cfg); })
                .catch(function() { arrancar(null); });

            function arrancar(cfg) {
                configuracion = cfg;
                if (cfg) {
                    params.data.syncDate = cfg.syncDate;
                    params.data.dispositivo = cfg.dispositivo;
                }
                if (!params.data.dispositivo) {
                    if (typeof device != 'undefined') {
                        params.data.dispositivo = device;
                    } else {
                        params.data.dispositivo = { uuid: 'WEB_' + window.app.data.user.username };
                    }
                }
                vig.narra('Iniciando sincronización…');
                vig.arrancar();
                // 2) Nos suscribimos y, SOLO cuando el SSE está conectado (evento "hello"),
                //    lanzamos la preparación; así los avisos de colección lista no se pierden.
                SincronizacionEvents.subscribe(handler);
                SincronizacionEvents.onReady(function() {
                    self.request.promise({
                        url: urlPaquete(),
                        method: 'POST',
                        contentType: 'application/json',
                        dataType: 'json',
                        crossDomain: true,
                        processData: false,
                        headers: cabeceras(),
                        data: JSON.stringify(params.data)
                    }).then(function(res) {
                        let data = res && res.data;
                        if (data && data.error) { terminar(data); return; }
                        let dto = data && data.dto;
                        if (!dto) { terminar({ error: 'Respuesta inesperada del servidor' }); return; }
                        if (!paqueteId && dto.paquete) paqueteId = dto.paquete;
                        if (!paqueteId) {
                            // En curso una sync antigua (streaming) sin paquete al que engancharse.
                            terminar({ error: 'Ya hay una sincronización en curso. Espera a que termine.' });
                            return;
                        }
                        // Sondeo del manifiesto: si el SSE pierde algún aviso, por aquí llega igual.
                        // Si un "fin" temprano ya lo arrancó (o ya terminó de preparar), no se
                        // crea otro: pisar el único asidero dejaría el primero corriendo para siempre.
                        if (!sondeo && !preparado) sondeo = setInterval(sondear, SONDEO_MS);
                        if (dto.enCurso) sondear();
                    }).catch(function(e) {
                        // API antigua sin el endpoint: que el llamante use el streaming.
                        if (e && (e.status == 404 || e.status == 405)) {
                            terminado = true;
                            vig.parar();
                            cerrarSse();
                            reject({ fallbackStreaming: true });
                            return;
                        }
                        terminar(e);
                    });
                });
            }
        }
        return promesa;
    },
    _catchError(e, reject) {
        if (typeof e == 'string') e = { error: e };
        if (typeof e == 'undefined') e = { error: 'Error desconocido' };
        if (e.xhr) {
            var { xhr, status, message } = e;
            if (status == 0) {
                // Offline
                reject({ error: 'Se necesita conexión a internet para sincronizar las inspecciones' });
            } else if (status == 401) {
                // Offline
                reject({ error: 'invalid_token'});
            } else {
                try {
                    reject(JSON.parse(xhr.response));
                } catch (e) {
                    reject(xhr.response);
                }
            }
        } else {
            reject(e);
        }
    },
    // Índices de la base local que el sync necesita para no recorrer colecciones enteras.
    // `id` es por lo que se borra (deleteMany $in) y se busca (findOne) al fusionar; sin él,
    // cada operación sobre clientes es un recorrido de decenas de miles de documentos. Hay que
    // volver a crearlos tras cada recarga: deleteAll hace `drop`, y drop se lleva los índices.
    INDICES_LOCALES: {
        clientes: ['id'],
        municipios: ['id'],
        inspecciones: ['id', 'fechaInsp'],
        formatos: ['id', 'codigo'],
        equipos: ['id']
    },
    // Crea los índices de una colección si no existen (createIndex es idempotente en Mongo).
    // Nunca falla hacia fuera: un índice que no se puede crear no debe tirar una sincronización.
    _asegurarIndices(collection) {
        const self = this;
        let campos = self.INDICES_LOCALES[collection];
        if (!campos || !window.db || typeof window.db.createIndex !== 'function') return Promise.resolve();
        let t0 = Date.now();
        let p = Promise.resolve();
        campos.forEach(function(campo) {
            p = p.then(function() {
                return window.db.createIndex(self.database, collection, campo).catch(function(e) {
                    console.warn('createIndex ' + collection + '.' + campo + ' falló', e);
                });
            });
        });
        return p.then(function() { console.log('índices ' + collection + ' OK en ' + (Date.now() - t0) + ' ms'); });
    },
    _reloadCollection(collection, list, resolve, reject) {
        const self = this;
        console.log("_reloadCollection: " + collection);
        window.db.deleteAll(self.database, collection)
            .then((result) => {
                console.log("deleteAll OK: " + collection);
                self._insertMany(collection, list, function() {
                    // Tras el drop los índices han desaparecido: se recrean para la próxima fusión.
                    self._asegurarIndices(collection).then(resolve, resolve);
                }, reject);
        }).catch((error) => reject(error));
    },
    // Documentos por llamada al plugin. El puente de Cordova serializa cada trozo entero, así que
    // los documentos grandes van en trozos pequeños para no bloquear el WebView con cadenas de
    // decenas de MB.
    TROZO_INSERT: { inspecciones: 5, formatos: 25 },
    _insertMany(collection, list, resolve, reject) {
        const self = this;
        if (list && list.length) {
            let total = list.length;
            let trozo = self.TROZO_INSERT[collection] || 1000;
            let tInicio = Date.now();
            let llamadas = 0;
            console.log("_insertMany: " + collection + " -> " + total + " (trozos de " + trozo + ")");
            let p2 = Promise.resolve();
            while (list.length) {
                const dataToSend = list.splice(0, trozo);
                p2 = p2.then(_ => new Promise(function(resolve, reject) {
                    let t0 = Date.now();
                    // El plugin (desde 0.0.3) acepta el trozo como cadena JSON: cruza el puente
                    // de Cordova de una pieza y en nativo se parsea una sola vez con el parser
                    // de BSON, en vez de org.json + toString + Document.parse por documento.
                    window.db.insertMany(self.database, collection, JSON.stringify(dataToSend))
                        .then((result) => {
                            llamadas++;
                            console.log("insertMany OK: " + collection + " " + dataToSend.length + " docs en " + (Date.now() - t0) + " ms");
                            resolve();
                        }).catch((error) => reject(error));
                }));
            }
            p2.then((_) => {
                console.log("_insertMany " + collection + ": " + total + " docs en " + llamadas + " llamadas, " + (Date.now() - tInicio) + " ms");
                resolve();
            }).catch(e => reject(e));
        } else {
            resolve();
        }
    },
    _replaceInCollection(collection, list, resolve, reject, syncDate) {
        const self = this;
        console.log("_replaceInCollection: " + collection);
        if (!list || !list.length) {
            resolve();
        } else {
            if (!!!syncDate) {
                self._reloadCollection(collection, list, resolve, reject);
            } else {
                if (list.length > 500) {
                    const ids = list.map(l => l.id);
                    let t0 = Date.now();
                    // Con índice en `id` el borrado por $in es directo; sin él, un recorrido entero.
                    self._asegurarIndices(collection)
                        .then(() => window.db.deleteMany(self.database, collection, { id: { $in: ids } }))
                        .then(res => {
                            console.log("deleteMany OK: " + collection + " " + ids.length + " ids en " + (Date.now() - t0) + " ms");
                            self._insertMany(collection, list, resolve, reject);
                        })
                        .catch(e => reject(e));
                } else {
                    self._asegurarIndices(collection)
                    .then(() => window.db.count(self.database, collection, {}))
                    .then((result) => {
                        if (result && result > 0) {
                            let p2 = Promise.resolve();
                            list.forEach(l => {
                                Utils.cleanObj(l);
                                p2 = p2.then(_ => new Promise(function(resolve, reject) {
                                    guardarDocumento(self.database, collection, {id: l.id}, l)
                                        .then((result) => {
                                            console.log("replaceOne OK: " + collection);
                                        resolve();
                                    }).catch((error) => reject(error));
                                }));
                            });
                            p2.then((_) => resolve()).catch(e => reject(e));
                        } else {
                            self._reloadCollection(collection, list, resolve, reject);
                        }
                    }).catch((error) => reject(error));
                }

            }
        }
    },
    _insertNotExistsInCollection(collection, list, resolve, reject) {
        const self = this;
        if (!list || !list.length) {
            resolve();
        } else {
            self._asegurarIndices(collection)
            .then(() => window.db.count(self.database, collection, {}))
            .then(async (result) => {
                if (result && result > 0) {
                    // Una sola consulta de los ids que ya están, en vez de un findOne por documento
                    // (cada uno era un recorrido de la colección sin índice). Los que faltan se
                    // insertan juntos; los planificados bajan antes sus documentos, uno a uno como
                    // hasta ahora para no abrir decenas de descargas a la vez.
                    let t0 = Date.now();
                    list.forEach(l => Utils.cleanObj(l));
                    let ids = list.map(l => l.id).filter(id => id != null);
                    try {
                        let existentes = await window.db.find(self.database, collection, { id: { $in: ids } }, {}, -1, -1, 'id');
                        let yaEstan = {};
                        (existentes || []).forEach(e => { if (e && e.id != null) yaEstan[e.id] = true; });
                        let nuevos = list.filter(l => !yaEstan[l.id]);
                        console.log("_insertNotExistsInCollection " + collection + ": " + nuevos.length + " nuevos de " + list.length + " (ids comprobados en " + (Date.now() - t0) + " ms)");
                        if (collection == 'inspecciones') {
                            for (const l of nuevos) {
                                if (l && l.estado && l.estado.estado && l.estado.estado == 'PLANIFICADO') {
                                    await self._downloadDocuments(l);
                                }
                            }
                        }
                        self._insertMany(collection, nuevos, resolve, reject);
                    } catch (error) {
                        reject(error);
                    }
                } else {
                    let promises = [];
                    if (collection == 'inspecciones') {
                        // let promises = [];
                        for (let i = 0; i < list.length; i++) {
                            let l = list[i];
                            if (l && l.estado && l.estado.estado && l.estado.estado == 'PLANIFICADO') {
                                // try {
                                    promises.push(self._downloadDocuments(l));
                                    // await self._downloadDocuments(l);
                                // } catch(e) {
                                //     console.log("ERROR EN LA DESCARGA DE DOCUMENTOS", e);
                                //     reject(e);
                                // }
                            }
                        }
                        // try {
                        //     await Promise.all(promises)
                        // } catch(e) {
                        //     console.log("ERROR DESCARGARDO DOCUMENTOS", e);
                        // }
                    }
                    if (promises.length) {
                        Promise.all(promises).then(() => {
                            self._reloadCollection(collection, list, resolve, reject);
                        }).catch((e) => {
                            console.log("ERROR DESCARGARDO DOCUMENTOS", e);
                            self._reloadCollection(collection, list, resolve, reject);
                        })
                    } else {
                        self._reloadCollection(collection, list, resolve, reject);
                    }
                }
            }).catch((error) => reject(error));
        }
    },
    _getAllDocuments(insp) {
        const self = this;
        let documentos = [];
        if (insp.documentos && insp.documentos.length) {
            documentos = [...documentos, ...insp.documentos];
        }
        let docCompro = self._getDocumentosComprobaciones(insp.comprobaciones);
        if (docCompro && docCompro.length) {
            documentos = [...documentos, ...docCompro];
        }
        let docTabs = self._getDocumentosTabs(insp.tabs);
        if (docTabs && docTabs.length) {
            documentos = [...documentos, ...docTabs];
        }
        return documentos;
    },
    _downloadDocuments(insp) {
        const self = this;
        let documentos = self._getAllDocuments(insp);
        return new Promise(function(resolve, reject) {
            console.log("Comenzando descarga de documentos de la inspección " + (insp.id || insp._id));
            // var p = Promise.resolve();
            let promises = [];
            if (documentos && documentos.length) {
                for (let i = 0; i < documentos.length; i++) {
                    let doc = documentos[i];
                    try {
                        promises.push(new Promise(function(resolve, reject) {
                            console.log("get Documento DOC " + (insp.id || insp._id), doc);
                            DocumentoOnline.getDocumentoScaled(doc, 'blob').then((blobData) => {
                                console.log("OBTENIDO Documento " + (insp.id || insp._id), doc);
                                if (blobData.size > 10000000) {
                                    doc.alreadySaved = true;
                                    doc.descargadoInicial = true;
                                    console.log("FIN DOC SIN GUARDAR" + (insp.id || insp._id), doc);
                                    resolve()
                                } else {
                                    DocumentoOffline.saveBlobInspeccion(blobData, insp, "Documentos/Descargados", doc.nombre + '.' + doc.extension)
                                        .then(({ filePath, localURL }) => {
                                            doc.alreadySaved = true;
                                            doc.descargadoInicial = true;
                                            doc._localURL = localURL;
                                            doc.offlinePath = filePath;
                                            console.log("FIN DOC " + (insp.id || insp._id), doc);
                                            resolve();
                                        }).catch((e) => {
                                            console.log("ERROR GUARDANDO BLOB DOCUMENTO: ", e);
                                            resolve();
                                        });
                                    // FileUtils.saveBlobFile(blobData, doc.nombre, doc.extension, '/documentos/', (resSave) => {
                                    //     console.log(resSave);
                                    //     doc.alreadySaved = true;
                                    //     doc.descargadoInicial = true;
                                    //     console.log("FIN DOC " + (insp.id || insp._id), doc);
                                    //     resolve();
                                    // }, reject, true);
                                }
                            }).catch((e) => {
                                console.log("ERROR DESCARGANDO DOCUMENTO: ", e);
                                resolve();
                            });
                        }));
                    } catch (e) {
                        console.log("ERROR DESCARGANDO DOCUMENTO: ", e);
                    }
                }
            }

            Promise.all(promises).then(() => {
                console.log("Todos los documentos descargados de la inspección " + (insp.id || insp._id));
                resolve();
            }).catch((e) => {
                console.log("Error en la descarga de documentos de la inspección " + (insp.id || insp._id), e);
                resolve();
            });
        });
    },
    _getDocumentosTabs(tabs) {
        const self = this;
        let documentos = [];
        if (tabs && tabs.length) {
            tabs.forEach(t => {
                if (t.items && t.items.length) {
                    t.items.forEach(t => {
                        let docCompro = self._getDocumentosComprobaciones(t.comprobaciones);
                        documentos = [...documentos, ...docCompro];
                        if (t.documentos && t.documentos.length) {
                            documentos = [...documentos, ...t.documentos];
                        }
                    });
                }
            });
        } 
        return documentos
    },
    _getDocumentosComprobaciones(comprobaciones) {
        const self = this;
        let documentos = [];
        if (comprobaciones && comprobaciones.length) {
            comprobaciones.forEach(c => {
                if (c.defectosSeleccionados && c.defectosSeleccionados.length) {
                    c.defectosSeleccionados.forEach(d => {
                        if (d.documentos && d.documentos.length) {
                            documentos = [...documentos, ...d.documentos];
                        }
                    });
                }
                if (c.documentos && c.documentos.length) {
                    documentos = [...documentos, ...c.documentos];
                }
                if (c.hijas && c.hijas.length) {
                    let docHijas = self._getDocumentosComprobaciones(c.hijas);
                    if (docHijas && docHijas.length) {
                        documentos = [...documentos, ...docHijas];
                    }
                }
            });
        } 
        return documentos
    },
    createIndex (collection, indexProperty) {
        const self = this;
        return new Promise(function(resolve, reject) {
            window.db.createIndex(self.database, collection, indexProperty)
                .then((result) => {
                    resolve(result);
                }).catch((error) => reject(error));
        });
    }
}

export default DatabaseModel;